import React, { lazy, Suspense } from "react";
import { useTheme } from "../hooks/useTheme";

const RawFloatingCode = lazy(() => import("./RawFloatingCode"));

const Hero = () => {
  const { isDarkMode } = useTheme();

  return (
    <section
      id="home"
      className={`min-h-screen flex items-center relative overflow-hidden transition-colors duration-700 ${
        isDarkMode ? "text-graphite-crystal" : "text-graphite-darkest"
      }`}
      aria-label="Hero section"
    >
      {/* Special Hero gradient overlay for visual interest */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${
          isDarkMode
            ? "bg-gradient-to-b from-graphite-darkest/80 to-transparent"
            : "bg-gradient-to-b from-graphite-lightest/80 to-transparent"
        }`}
        aria-hidden="true"
      ></div>
      {/* Floating code blocks - lazy loaded to reduce initial bundle */}
      <Suspense fallback={null}>
        <RawFloatingCode />
      </Suspense>

      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20 text-center pt-20 sm:pt-24 md:pt-28 lg:pt-32 pb-16 relative z-20">
        <article className="max-w-5xl mx-auto">
          <h1
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-6 leading-[1.1] transition-colors duration-700 ${
              isDarkMode ? "text-white" : "text-graphite-darkest"
            }`}
          >
            Full-Stack Developer & Digital Creator
          </h1>

          <p
            className={`text-lg sm:text-xl lg:text-2xl mb-8 leading-relaxed max-w-4xl mx-auto transition-colors duration-700 ${
              isDarkMode ? "text-gray-200" : "text-graphite-medium"
            }`}
          >
            Expert web development services crafting innovative web
            applications, engaging games, and next-generation Web3 experiences.
            From concept to deployment, we bring your digital vision to life
            with clean code and creative solutions.
          </p>

          <nav
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            aria-label="Call to action"
          >
            <a
              href="#services"
              className={`group w-full sm:w-auto inline-block py-3 sm:py-4 px-6 sm:px-8 rounded-lg no-underline font-semibold transition-all duration-300 ease-in-out transform hover:scale-105 text-center relative overflow-hidden ${
                isDarkMode
                  ? "bg-gradient-to-r from-graphite-charcoal to-graphite-slate text-white hover:from-graphite-slate hover:to-graphite-silver shadow-lg shadow-graphite-void/70"
                  : "bg-gradient-to-r from-graphite-medium to-graphite-dark text-white hover:from-graphite-dark hover:to-graphite-darker shadow-lg shadow-graphite-medium/30"
              }`}
              aria-label="View our development services"
            >
              <span className="relative z-10">Our Services</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                aria-hidden="true"
              ></div>
            </a>

            <a
              href="#contact"
              className={`group w-full sm:w-auto inline-block py-3 sm:py-4 px-6 sm:px-8 rounded-lg no-underline font-semibold transition-all duration-300 ease-in-out text-center relative overflow-hidden border-2 ${
                isDarkMode
                  ? "border-white text-white hover:bg-white hover:text-graphite-darkest bg-transparent"
                  : "border-graphite-medium text-graphite-medium hover:bg-graphite-medium hover:text-white bg-transparent"
              }`}
              aria-label="Contact us for a project"
            >
              <span className="relative z-10">Let's Collaborate</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-graphite-shine/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                aria-hidden="true"
              ></div>
            </a>
          </nav>
        </article>
      </div>
    </section>
  );
};

export default Hero;
